import { useState } from 'react';
import { api } from '../../lib/api';
import { Modal } from '../../components/primitives';

// ── TOTP modal — setup when off, disable (reset) when on ──────────────────

export function TotpModal({
  totp,
  onClose,
}: {
  totp: { enabled: boolean; pending: boolean } | null;
  onClose: () => void;
}) {
  const [setup, setSetup] = useState<{ secret: string; otpauth_url: string } | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enabled = !!totp?.enabled;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={enabled ? 'Reset TOTP' : 'Set up TOTP'}
      mobile="half"
      context="SETTINGS"
      width={520}
      onClose={onClose}
      hint={enabled ? 'Disabling requires password + current code' : 'Scan or enter the secret, then confirm'}
      foot={
        <>
          <button className="btn" onClick={onClose}>
            {setup ? 'Done later' : 'Close'}
          </button>
          {enabled ? (
            <button
              className="btn danger"
              disabled={busy || !password || code.length < 6}
              onClick={() =>
                run(async () => {
                  await api('/api/auth/totp/disable', { method: 'POST', json: { password, code } });
                  onClose();
                })
              }
            >
              Disable TOTP →
            </button>
          ) : setup ? (
            <button
              className="btn primary"
              disabled={busy || code.length < 6}
              onClick={() =>
                run(async () => {
                  await api('/api/auth/totp/enable', { method: 'POST', json: { code } });
                  onClose();
                })
              }
            >
              Confirm code →
            </button>
          ) : (
            <button
              className="btn primary"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  const r = await api<{ secret: string; otpauth_url: string }>('/api/auth/totp/setup', {
                    method: 'POST',
                  });
                  setSetup(r);
                })
              }
            >
              {totp?.pending ? 'Restart setup →' : 'Generate secret →'}
            </button>
          )}
        </>
      }
    >
      <div className="form-grid">
        {enabled ? (
          <>
            <div className="f-field span-2">
              <span className="f-label">
                Password <span className="req">*</span>
              </span>
              <input
                className="f-input"
                type="password"
                value={password}
                aria-label="Password"
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
            </div>
            <div className="f-field">
              <span className="f-label">
                Current code <span className="req">*</span>
              </span>
              <input
                className="f-input mono"
                inputMode="numeric"
                placeholder="123456"
                value={code}
                aria-label="Current authenticator code"
                autoComplete="one-time-code"
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <p className="span-2" style={{ fontSize: 12, color: 'var(--text-dim)' }}>
              After disabling, reopen this dialog to generate a fresh secret.
            </p>
          </>
        ) : setup ? (
          <>
            <div className="f-field span-2">
              <span className="f-label">Secret — add to your authenticator</span>
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  padding: '10px 12px',
                  background: 'var(--panel-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  wordBreak: 'break-all',
                }}
              >
                {setup.secret}
              </div>
            </div>
            <div className="f-field span-2" style={{ fontSize: 12 }}>
              <a href={setup.otpauth_url} style={{ color: 'var(--accent)' }}>
                …or open directly in an authenticator app
              </a>
            </div>
            <div className="f-field">
              <span className="f-label">
                First code <span className="req">*</span>
              </span>
              <input
                className="f-input mono"
                inputMode="numeric"
                placeholder="123456"
                value={code}
                aria-label="First authenticator code"
                autoComplete="one-time-code"
                onChange={(e) => setCode(e.target.value)}
                autoFocus
              />
            </div>
          </>
        ) : (
          <p className="span-2" style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>
            Generates a fresh TOTP secret for your authenticator app. Login will require a code once
            confirmed.
          </p>
        )}
        {error && <p className="error-text span-2">{error}</p>}
      </div>
    </Modal>
  );
}
